// ============================================================================
// pwa-links-client-mod -- PwaLinksApiClient
//
// HTTP API 客户端，封装对 /api/pwa-links/* 和管理接口的调用。
//
// 通过 hvsin DI 机制注册为 Service，经 IClientServiceRegistryService
// 注册到 React Context，业务组件通过 useClientServiceByTaghook 消费。
// ============================================================================
/* -------------------------------------------------------------------------- */
/* 工具函数                                                                    */
/* -------------------------------------------------------------------------- */
function getAuthToken() {
    if (typeof window === 'undefined')
        return null;
    return localStorage.getItem('accessToken');
}
/* -------------------------------------------------------------------------- */
/* PwaLinksApiClient                                                          */
/* -------------------------------------------------------------------------- */
export class PwaLinksApiClient {
    constructor(deps) {
        if (deps && typeof deps === 'object' && 'fetchService' in deps) {
            this.fetchService = deps.fetchService ?? null;
        }
        else {
            this.fetchService = deps ?? null;
        }
    }
    async doFetch(url, options) {
        if (this.fetchService) {
            return this.fetchService.fetch(url, options);
        }
        const response = await globalThis.fetch(url, {
            method: options?.method ?? 'GET',
            headers: options?.headers,
            body: options?.body,
        });
        return {
            status: response.status,
            ok: response.ok,
            json: () => response.json(),
            text: () => response.text(),
        };
    }
    get authHeaders() {
        const token = getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }
    async request(url, method, body) {
        const response = await this.doFetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...this.authHeaders,
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        if (!response.ok) {
            let message = `Request failed: ${response.status}`;
            try {
                const data = await response.json();
                if (data && typeof data === 'object' && 'error' in data) {
                    message = data.error;
                }
            }
            catch { /* ignore */ }
            throw new Error(message);
        }
        return response.json();
    }
    buildQuery(params) {
        if (!params)
            return '';
        const searchParams = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
            if (v != null)
                searchParams.set(k, String(v));
        }
        const qs = searchParams.toString();
        return qs ? `?${qs}` : '';
    }
    // === 公开 API ===
    async getPublicLinks(params) {
        const query = this.buildQuery(params);
        return this.request(`/api/pwa-links${query}`, 'GET');
    }
    async getPublicLink(id) {
        return this.request(`/api/pwa-links/${id}`, 'GET');
    }
    async getCategories() {
        return this.request('/api/pwa-links-categories', 'GET');
    }
    async recordClick(id) {
        return this.request(`/api/pwa-links/${id}/record-click`, 'POST');
    }
    // === 管理 API ===
    async getAllAdmin(params) {
        const query = this.buildQuery(params);
        return this.request(`/api/admin/pwa-links${query}`, 'GET');
    }
    async createLink(params) {
        return this.request('/api/admin/pwa-links', 'POST', params);
    }
    async updateLink(id, params) {
        return this.request(`/api/admin/pwa-links/${id}`, 'PATCH', params);
    }
    async deleteLink(id) {
        return this.request(`/api/admin/pwa-links/${id}`, 'DELETE');
    }
    async toggleLink(id) {
        return this.request(`/api/admin/pwa-links/${id}/toggle`, 'POST');
    }
    async batchImport(params) {
        return this.request('/api/admin/pwa-links/batch-import', 'POST', params);
    }
    // 分类管理
    async createCategory(params) {
        return this.request('/api/admin/pwa-links-categories', 'POST', params);
    }
    async updateCategory(id, params) {
        return this.request(`/api/admin/pwa-links-categories/${id}`, 'PATCH', params);
    }
    async deleteCategory(id) {
        return this.request(`/api/admin/pwa-links-categories/${id}`, 'DELETE');
    }
}
//# sourceMappingURL=pwa-links-api-client.js.map